import * as React from "react";
import PropTypes from "prop-types";
import { useAutocomplete } from "@mui/base/useAutocomplete";
import styled, { css } from "styled-components/macro";
import { matchSorter } from "match-sorter";
import { mediaQueries } from "../styles/media.js";
import { useMergedRefs } from "../utils/refs.js";
import { mergeHandlers } from "../utils/events.js";

const MERGED_INPUT_HANDLERS = [
  "onBlur",
  "onFocus",
  "onChange",
  "onKeyDown",
  "onKeyUp",
  "onMouseDown",
  "onClick",
  "onInput",
  "onCompositionStart",
  "onCompositionEnd",
  "onPaste",
];

/**
 * Autocomplete component using @mui/base useAutocomplete hook.
 */

const ClearIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Root = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled.input`
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: 3em;
  margin-top: 0;
  padding: var(--juno-space-2) var(--juno-space-3);
  border: 2px solid var(--juno-color-border);
  border-radius: 0;
  font-size: var(--juno-font-size-body-sm);
  line-height: 1.5rem;
  font-weight: var(--juno-font-weight-light);
  appearance: none;

  ${mediaQueries.md} {
    font-size: var(--juno-font-size-body-md);
    line-height: 2.1875rem;
  }

  &:focus-visible {
    outline: var(--juno-focus-outline-width) solid var(--juno-color-focus);
    outline-offset: var(--juno-focus-outline-offset);
  }

  ${(props) =>
    props.$error &&
    css`
      border-width: 4px;
      border-color: var(--juno-color-error);
    `};

  ${(props) =>
    props.disabled &&
    css`
      font-weight: var(--juno-font-weight-bold);
      background: none;
    `};
`;

const ActionButtons = styled.div`
  position: absolute;
  right: var(--juno-space-3);
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: var(--juno-space-2);
  z-index: 1;
  pointer-events: none;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--juno-space-6);
  height: var(--juno-space-6);
  border: none;
  background: var(--juno-color-very-pale-grey);
  color: var(--juno-color-grey);
  cursor: pointer;
  padding: 0;
  border-radius: var(--juno-radius-sm);
  pointer-events: auto;

  &:hover {
    color: var(--juno-color-white);
    background-color: var(--juno-color-accent);
  }

  /* Keep the 2px ring and 2px offset from spacingScale[0.5] on master. The
     shared focus tokens give 3px with no offset, which is a visual change.
     Align these buttons with the focus tokens in a later batch. */
  &:focus-visible {
    outline: var(--juno-space-half) solid var(--juno-color-focus);
    outline-offset: var(--juno-space-half);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  svg {
    width: var(--juno-space-4);
    height: var(--juno-space-4);
    transform: rotate(${(props) => (props.$open ? "180deg" : "0deg")});
  }
`;

const Popper = styled.div`
  position: absolute;
  z-index: 2000;
  box-sizing: border-box;
  padding: var(--juno-space-2);
  background-color: var(--juno-color-white);
  border: 2px solid var(--juno-color-border);
  box-shadow: var(--juno-shadow-dropdown);
  max-height: var(--juno-dropdown-max-height);
  overflow: auto;
  margin-top: var(--juno-dropdown-side-offset);

  ${(props) =>
    !props.$open &&
    css`
      display: none;
    `};
`;

const Listbox = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 100% !important;
`;

const Option = styled.li`
  padding: var(--juno-space-2);
  cursor: pointer;
  border: none;
  font-size: var(--juno-font-size-body-md);
  line-height: 1.5;
  outline: 0;
  user-select: none;
  display: block;
  position: relative;

  &:nth-child(even) {
    background: var(--juno-color-very-pale-grey);
  }

  &:hover,
  &.highlighted,
  &.Mui-focused {
    background-color: var(--juno-color-accent);
    color: var(--juno-color-white);
    outline: 0;
  }
`;

const NoOptions = styled.li`
  padding: var(--juno-space-2);
  color: var(--juno-color-text-secondary);
  cursor: not-allowed;
`;

const Autocomplete = React.forwardRef(function Autocomplete(props, ref) {
  const {
    disableClearable = false,
    disabled = false,
    readOnly = false,
    id,
    placeholder,
    autoHighlight = false,
    sortOptions,
    filterOptions,
    filterKeys,
    error,
    slotProps = {},
    noOptionsText = "No results",
    onChange,
    value,
    defaultValue,
    getOptionLabel,
    renderOption,
  } = props;

  // Default option label function - just returns the label
  const defaultGetOptionLabel = React.useCallback((option) => option.label, []);

  // Default option renderer - renders label text only
  const defaultRenderOption = React.useCallback((option) => option.label, []);

  const {
    getRootProps,
    getInputProps,
    getClearProps,
    getPopupIndicatorProps,
    getListboxProps,
    getOptionProps,
    dirty,
    popupOpen,
    anchorEl,
    setAnchorEl,
    groupedOptions,
  } = useAutocomplete({
    options: props.options,
    onChange,
    value,
    defaultValue,
    getOptionLabel: getOptionLabel || defaultGetOptionLabel,
    filterOptions:
      filterOptions ||
      ((options, { inputValue }) => {
        const filtered = matchSorter(options, inputValue, {
          keys: filterKeys || ["label"],
        });
        return sortOptions ? sortOptions(filtered) : filtered;
      }),
    autoHighlight,
  });

  const hasClearIcon = !disableClearable && !disabled && dirty && !readOnly;
  const hasValue = !!props.value;

  // Extract the ref from getInputProps to avoid overwriting it
  const { ref: inputRef, ...muiInputProps } = getInputProps();

  // Get custom input props from slotProps (memoized to keep stable deps)
  const customInputProps = React.useMemo(() => slotProps.input || {}, [slotProps.input]);

  // Merge the anchor ref (for popper positioning) with MUI's input ref
  const anchorRef = React.useCallback((node) => setAnchorEl(node), [setAnchorEl]);
  const mergedRef = useMergedRefs(anchorRef, inputRef);

  // Merge custom input props with MUI input props
  const mergedInputProps = React.useMemo(() => {
    const merged = { ...muiInputProps, ...customInputProps };

    // Merge event handlers to call both MUI and custom handlers
    MERGED_INPUT_HANDLERS.forEach((handler) => {
      const muiHandler = muiInputProps[handler];
      const customHandler = customInputProps[handler];
      if (muiHandler || customHandler) {
        merged[handler] = mergeHandlers(muiHandler, customHandler);
      }
    });

    return merged;
  }, [muiInputProps, customInputProps]);

  return (
    <React.Fragment>
      <Root {...getRootProps()} ref={ref}>
        <StyledInput
          {...mergedInputProps}
          ref={mergedRef}
          id={id}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          $error={error}
          $hasValue={hasValue}
          data-1p-ignore={true} // Prevent 1Password autofill icon
        />

        <ActionButtons>
          {hasClearIcon && (
            <IconButton {...getClearProps()} aria-label="Clear selection" type="button">
              <ClearIcon />
            </IconButton>
          )}

          <IconButton {...getPopupIndicatorProps()} $open={popupOpen} disabled={disabled} aria-label="Toggle dropdown">
            <ChevronDownIcon />
          </IconButton>
        </ActionButtons>
      </Root>
      {anchorEl ? (
        <Popper
          $open={popupOpen}
          style={{
            width: anchorEl.offsetWidth,
            maxWidth: "100%",
          }}
        >
          <Listbox {...getListboxProps()} {...slotProps.listbox}>
            {groupedOptions.length === 0 && <NoOptions>{noOptionsText}</NoOptions>}
            {groupedOptions.map((option, index) => {
              const optionProps = getOptionProps({ option, index });
              const optionRenderer = renderOption || defaultRenderOption;
              return (
                <Option {...optionProps} key={option.value}>
                  {optionRenderer(option)}
                </Option>
              );
            })}
          </Listbox>
        </Popper>
      ) : null}
    </React.Fragment>
  );
});

Autocomplete.propTypes = {
  /**
   * If `true`, the first option is automatically highlighted.
   */
  autoHighlight: PropTypes.bool,
  /**
   * If `true`, the input can't be cleared.
   * @default false
   */
  disableClearable: PropTypes.bool,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * Array of options.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /**
   * If `true`, the component becomes readonly.
   * @default false
   */
  readOnly: PropTypes.bool,
  /**
   * Placeholder text for the input.
   */
  placeholder: PropTypes.string,
  /**
   * Function to apply custom sorting instead of default alphabetical.
   */
  sortOptions: PropTypes.func,
  /**
   * Function to filter options. If not provided, uses default filtering with filterKeys.
   */
  filterOptions: PropTypes.func,
  /**
   * Keys to use for filtering when using default filterOptions (defaults to ['label']).
   * Pass ['label', 'aliases'] to also search through aliases field.
   */
  filterKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * HTML id attribute for the input element.
   */
  id: PropTypes.string,
  /**
   * If `true`, shows error styling on the input.
   */
  error: PropTypes.bool,
  /**
   * The selected value.
   */
  value: PropTypes.any,
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue: PropTypes.any,
  /**
   * Callback fired when the value changes.
   */
  onChange: PropTypes.func,
  /**
   * Props to pass to internal slots (input, listbox).
   */
  slotProps: PropTypes.shape({
    input: PropTypes.object,
    listbox: PropTypes.object,
  }),
  /**
   * Text to display when no options match.
   * @default 'No results'
   */
  noOptionsText: PropTypes.string,
  /**
   * Function to convert an option object to a string for the input display.
   * Defaults to option => option.label
   */
  getOptionLabel: PropTypes.func,
  /**
   * Function to render the content of an option in the dropdown.
   * Receives the option object and should return a React node.
   * Defaults to option => option.label
   */
  renderOption: PropTypes.func,
};

export default Autocomplete;
